import { useEffect, useMemo, useState } from 'react';
import Modal from '../components/Modal.tsx';
import { api, errorMessage } from '../lib/api.ts';
import { isBrowsable } from '../lib/deckFilter.ts';
import { relativeTime } from '../lib/format.ts';
import { Search } from '../lib/icons.tsx';
import type { DecksPayload } from '../../shared/api.ts';
import type { DeckSummary } from '../../shared/deck.ts';
import type { LibraryItem } from '../../shared/api.ts';

type InsertIntoDeckModalProps = {
  item: LibraryItem;
  onClose: () => void;
  /** Runs the insert and reports what happened. The route owns the request so
   *  the toast, the busy flag and the undo all stay where every other mutation
   *  on this page keeps them. */
  onInsert: (deck: DeckSummary) => Promise<void>;
  busy: boolean;
};

/**
 * "Insert into deck", from the Library rather than from inside a deck.
 *
 * The endpoint is the one the editor's Add-a-slide gallery already uses
 * (`POST /api/slides/:slug/section/from-library`); all this dialog adds is the
 * missing half of the sentence, which is WHICH deck. It lists decks and does
 * nothing else: no filters, no facets, no covers. `?covers=1` is the
 * dashboard's alone, because it is the only page that pays for that render.
 */
export default function InsertIntoDeckModal({ item, onClose, onInsert, busy }: InsertIntoDeckModalProps) {
  const [decks, setDecks] = useState<DeckSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .get<DecksPayload>('/api/decks')
      .then((d) => {
        if (cancelled) return;
        // Newest first: the deck you were just working in is the one you are
        // most likely inserting into.
        setDecks((d.decks || []).filter(isBrowsable).sort((a, b) => b.lastEditedAt - a.lastEditedAt));
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, 'Could not load your decks'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !decks) return decks;
    return decks.filter((d) => d.title.toLowerCase().includes(needle) || d.slug.includes(needle));
  }, [decks, q]);

  return (
    <Modal
      title={`Insert "${item.name}"`}
      titleClassName="modal__title--sm"
      className="modal--confirm"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <p className="muted">
        {item.count} slide{item.count === 1 ? '' : 's'} copied into the deck you pick. The copy is a snapshot, so later
        edits to this item leave that deck alone.
      </p>

      <div className="page-search insert-deck__search">
        <Search size={15} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your decks…"
          aria-label="Search your decks"
          autoFocus
        />
      </div>

      {error && <p className="error">{error}</p>}
      {!decks && !error && <p className="muted">Loading decks…</p>}
      {shown && shown.length === 0 && <p className="muted">No deck matches that.</p>}

      {shown && shown.length > 0 && (
        <ul className="insert-deck__list" role="list">
          {shown.map((deck) => (
            <li key={deck.slug}>
              <button
                type="button"
                className="menu__item insert-deck__deck"
                disabled={busy}
                onClick={() => void onInsert(deck)}
              >
                <span className="insert-deck__name">{deck.title}</span>
                <span className="muted insert-deck__meta">
                  {deck.slideCount} slides · {relativeTime(deck.lastEditedAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
